import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import useHttpClient from './useHttpClient';

interface ICommentBody {
  body: string;
  authorId: {
    native: number;
  };
}

interface ICommentResponse {
  commentId: {
    native: number;
  };
}

interface ICommentPayload {
  addComment: (noteId: number, commentBody: ICommentBody) => Promise<ICommentResponse>;
  loading: boolean;
  error?: Error;
  called: boolean;
  commentRes?: {
    commentId: {
      native: number;
    };
  };
}

const useMutateComment = (): ICommentPayload => {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  const { mutateAsync, error, isPending, isSuccess, isError, data } = useMutation({
    mutationFn: async ({ noteId, commentBody }: { noteId: number; commentBody: ICommentBody }) => {
      const response = await httpClient.post(`/notes/${noteId}/comments`, commentBody);
      return response.data as ICommentResponse;
    },
    onSuccess: (result, variables) => {
      // Invalidate note comments for this specific note
      queryClient.invalidateQueries(['noteComments', variables.noteId]);
      // Also invalidate the note itself to update comment count
      queryClient.invalidateQueries(['note', variables.noteId]);
    },
  });

  const addComment = useCallback(async (noteId: number, commentBody: ICommentBody) => {
    return mutateAsync({ noteId, commentBody });
  }, [mutateAsync]);

  return {
    addComment,
    error: error as Error,
    loading: isPending,
    called: isSuccess || isError,
    commentRes: data,
  };
};

export default useMutateComment;